import React, { useState, useEffect, useContext } from 'react';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

// COMPONENTS
import TableComponent from 'components/common/tableComponent';
import ToggleSwitch from 'components/buttons/ToggleSwitch';
import SearchBar from 'components/administration/searchBarComponent';

// ICONS
import { Trash2, UserPlus, Check, X, Delete } from 'lucide-react';

// SERVICES
import { getWhitelistStatus, upsertWhitelistStatus, getUsersWhitelistAnnouncement, insertUserWhitelistAnnouncement, deleteUserWhitelistAnnouncement } from 'services/settingService';
import { getIDSecurityGroup, getUsersByIDSecurityGroup } from 'services/microsoftGraphService';


function ManagementAnnouncements() {
    const { addToast } = useContext(ToastContext);
    const groupName = process.env.REACT_APP_GROUP_SECU_INTERNE;
    const [users, setUsers] = useState([]);
    const [isWhitelistActive, setIsWhitelistActive] = useState(false);
    const [whitelistedUsers, setWhitelistedUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [newDisplayName, setNewDisplayName] = useState('');
    const [error, setError] = useState(null);
    const [confirmingDeleteUser, setConfirmingDeleteUser] = useState(null);

    // États pour l'auto-complétion du displayName
    const [displayNameSuggestions, setDisplayNameSuggestions] = useState([]);
    const [showDisplayNameSuggestions, setShowDisplayNameSuggestions] = useState(false);

    // Charger les données initiales
    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            setError(null);
            try {
                const [status, users, groupId] = await Promise.all([
                    getWhitelistStatus(),
                    getUsersWhitelistAnnouncement(),
                    getIDSecurityGroup(groupName)
                ]);
                if (status.data) {
                    const statusValue = status.data.STATUS_WHITELIST === "1" ? true : false;
                    setIsWhitelistActive(statusValue);
                }
                setWhitelistedUsers(users.data);
                const usersGroup = await getUsersByIDSecurityGroup(groupId.data);
                setUsers(usersGroup.data);
            } catch (err) {
                console.error("Erreur lors du chargement des données:", err);
                addToast(
                    'Erreur',
                    'Impossible de charger les données de la whitelist.',
                    null,
                    null,
                    'error'
                );
                setIsWhitelistActive(false);
                setWhitelistedUsers([]);
                setUsers([]);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const insertUserWhitelist = async (username) => {
        try {
            const response = await insertUserWhitelistAnnouncement(username);
            return response;
        } catch (error) {
            console.error("Erreur lors de l'ajout d'un utilisateur à la whitelist:", error);
            addToast(
                'Erreur',
                'Impossible d\'ajouter un utilisateur à la whitelist.',
                null,
                null,
                'error'
            );
            throw error;
        }
    };

    const removeUserWhitelist = async (setting_id) => {
        try {
            const response = await deleteUserWhitelistAnnouncement(setting_id);
            return response;
        } catch (error) {
            console.error("Erreur lors de la suppression d'un utilisateur de la whitelist:", error);
            addToast(
                'Erreur',
                'Impossible de supprimer un utilisateur de la whitelist.',
                null,
                null,
                'error'
            );
            throw error;
        }
    };    

    // Gérer le changement de statut de la whitelist
    const handleToggleWhitelist = async () => {
        const newStatus = !isWhitelistActive;
        setIsWhitelistActive(newStatus);
        try {
            const result = await upsertWhitelistStatus(newStatus);
            if (result.status === 400) {
                setIsWhitelistActive(!newStatus);
                addToast(
                    'Erreur',
                    'Échec de la mise à jour du statut de la whitelist.',
                    null,
                    null,
                    'error'
                );
            }
        } catch (err) {
            console.error("Erreur lors de la mise à jour du statut:", err);
            // Rollback en cas d'erreur réseau/API
            setIsWhitelistActive(!newStatus);
            addToast(
                'Erreur',
                'Impossible de mettre à jour le statut de la whitelist.',
                null,
                null,
                'error'
            );
        }
    };

    const handleDisplayNameChange = (e) => {
        const value = e.target.value;
        setNewDisplayName(value);

        if (value.trim() === '') {
            setDisplayNameSuggestions([]);
            setShowDisplayNameSuggestions(false);
            return;
        }

        const lowerValue = value.toLowerCase();
        const suggestions = users
            .map(user => user.displayName) // Obtenir tous les displayNames
            .filter((displayName, index, self) => // Filtrer et dédoublonner
                displayName.toLowerCase().includes(lowerValue) && self.indexOf(displayName) === index
            );
        
        setDisplayNameSuggestions(suggestions.slice(0, 5)); // Limiter à 5 suggestions
        setShowDisplayNameSuggestions(suggestions.length > 0);
    };
    
    const handleSuggestionClick = (suggestion) => {
        setNewDisplayName(suggestion);
        setShowDisplayNameSuggestions(false);
    };

    const handleClearDisplayName = () => {
        setNewDisplayName('');
        setDisplayNameSuggestions([]);
        setShowDisplayNameSuggestions(false);
    };

    // Demande de confirmation avant suppression
    const handleRemoveUser = (userToRemove) => {
        setConfirmingDeleteUser(userToRemove.SIT_ID);
    };

    // Annulation de la suppression
    const cancelDeleteUser = () => {
        setConfirmingDeleteUser(null);
    };

    // Suppression confirmée d'un utilisateur
    const handleConfirmedDeleteUser = async (userToDelete) => {
        const originalUsers = [...whitelistedUsers];
        // Optimistic UI update
        setWhitelistedUsers(prevUsers => prevUsers.filter(user => user.SIT_ID !== userToDelete.SIT_ID));
        setConfirmingDeleteUser(null);

        try {
            const result = await removeUserWhitelist(userToDelete.SIT_ID);
            if (result.status === 400) {
                // Rollback si l'API échoue
                setWhitelistedUsers(originalUsers);
                addToast(
                    'Erreur',
                    "Échec de la suppression de l'utilisateur.",
                    null,
                    null,
                    'error'
                );
            } else {
                 addToast(
                    'Succès',
                    `L'utilisateur ${userToDelete.USERNAME} a été supprimé avec succès.`,
                    null,
                    null,
                    'success'
                );
            }
        } catch (err) {
            console.error("Erreur lors de la suppression:", err);
             // Rollback en cas d'erreur réseau/API
            setWhitelistedUsers(originalUsers);
            addToast(
                'Erreur',
                'Une erreur est survenue lors de la suppression.',
                null,
                null,
                'error'
            );
        }
    };

    // Gérer l'ajout d'un utilisateur
    const handleAddUser = async (e) => {
        e.preventDefault(); // Empêcher le rechargement de la page
        if (!newDisplayName.trim()) {
            addToast(
                'Erreur',
                'Veuillez entrer un nom d\'affichage.',
                null,
                null,
                'error'
            );
            return;
        }
        
        const username = newDisplayName.trim();

        // Vérifier si l'utilisateur existe déjà dans la whitelist
        if (whitelistedUsers.some(user => user.USERNAME.toLowerCase() === username.toLowerCase())) {
            addToast(
                'Erreur',
                `L'utilisateur ${username} est déjà dans la whitelist.`,
                null,
                null,
                'error'
            );
           return;
        }

        // Vérifier si le displayName saisi existe dans la liste des utilisateurs globaux (users)
        if (!users.some(user => user.displayName.toLowerCase() === username.toLowerCase())) {
            addToast(
                'Erreur',
                `L'utilisateur "${username}" n'est pas reconnu. Veuillez sélectionner un utilisateur depuis les suggestions ou vérifier l'orthographe.`,
                null,
                null,
                'error'
            );
            return;
        }

        try {
            const result = await insertUserWhitelist(username);
            if (result.status === 200) {
                setWhitelistedUsers(prevUsers => [...prevUsers, result.data]);
                setNewDisplayName(''); // Réinitialiser le champ
            } else {
                addToast(
                    'Erreur',
                    "Échec de l'ajout de l'utilisateur.",
                    null,
                    null,
                    'error'
                );
            }
        } catch (err) {
            console.error("Erreur lors de l'ajout:", err);
            addToast(
                'Erreur',
                "Une erreur est survenue lors de l'ajout.",
                null,
                null,
                'error'
            );
        }
    };

    // Définition des colonnes pour TableComponent
    const columns = [
        {
            key: 'user',
            label: 'Utilisateur',
            type: 'profil',
        },
        {
            key: 'addedAt',
            label: 'Date d\'ajout',
            type: 'date',
        },
        {
            key: 'actions',
            label: 'Actions',
            type: 'action',
        },
    ];

    // Préparer les données pour TableComponent
    const filteredUsers = whitelistedUsers.filter(user => 
        user.USERNAME.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const tableRows = filteredUsers.map(user => {
        const isConfirming = confirmingDeleteUser === user.SIT_ID;

        // Trouver l'utilisateur correspondant dans la liste globale pour obtenir l'e-mail
        const fullUserDetails = users.find(u => u.displayName === user.USERNAME);

        let profilePicSrc;
        if (fullUserDetails && fullUserDetails.mail) {
            if (fullUserDetails.mail === 'communication.interne@altitudeinfra.fr') {
                profilePicSrc = `https://ui-avatars.com/api/?name=${encodeURIComponent(fullUserDetails.displayName.replace('.', ' '))}&size=40&background=random&color=fff`;
            } else {
                profilePicSrc = `https://bes-sso.altitudeinfra.fr/avatar/${fullUserDetails.mail}`;
            }
        } else {
            // Fallback si l'e-mail n'est pas trouvé ou si fullUserDetails n'existe pas
            profilePicSrc = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.USERNAME.replace('.', ' '))}&size=40&background=random&color=fff`;
        }

        return {
            ...user,
            SIT_ID: user.SIT_ID,
            user: {
                src: profilePicSrc,
                alt: `Avatar de ${user.USERNAME}`,
                username: user.USERNAME,
            },
            addedAt: user.ADDED_AT,
            actions: isConfirming ? [
                {
                    icon: Check,
                    variant: 'success',
                    onClick: () => handleConfirmedDeleteUser(user),
                    label: 'Confirmer la suppression'
                },
                {
                    icon: X,
                    variant: 'error',
                    onClick: cancelDeleteUser,
                    label: 'Annuler'
                }
            ] : [
                {
                    icon: Trash2,
                    variant: 'error',
                    onClick: () => handleRemoveUser(user),
                    label: `Supprimer ${user.USERNAME}`
                }
            ]
        };
    });


    return (
        <div className="h-full flex flex-col">
            <div className="flex flex-col lg:flex-row gap-6 h-full">

                {/* Colonne de Gauche */}
                <div className="flex flex-col gap-6 lg:w-1/3">
                     {/* Section Activation Whitelist */}
                    <div className="p-4 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark">
                         <div className="flex items-center justify-between">
                            <span className="text-gray-700 dark:text-gray-300 font-medium">Mode Whitelist pour les annonces</span>
                            <ToggleSwitch
                                isActive={isWhitelistActive}
                                onChange={handleToggleWhitelist}
                             />
                        </div>
                    </div>

                    {/* Section Ajout Utilisateur */}
                    <form autoComplete="off" onSubmit={handleAddUser} className="flex flex-col gap-4 p-4 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark">
                        <h3 className="text-xl font-semibold text-gray-800 dark:text-blanc mb-2">Ajouter un utilisateur</h3>
                        <div className="relative ml-4">
                            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom d'affichage</label>
                            <input
                                type="text"
                                id="displayName"
                                value={newDisplayName}
                                onChange={handleDisplayNameChange}
                                onBlur={() => setTimeout(() => setShowDisplayNameSuggestions(false), 100)} // Pour permettre le clic sur la suggestion
                                placeholder="Nom d'affichage de l'utilisateur"
                                className="w-full px-3 py-2 rounded-md border border-gray-300 dark:bg-dark-secondary dark:text-blanc focus:outline-none"
                                required
                            />
                            {newDisplayName && (
                                <Delete
                                    className="absolute right-3 top-9 text-noir dark:text-blanc hover:text-orange dark:hover:text-orange cursor-pointer"
                                    size={20} // Ajuster la taille si nécessaire
                                    onClick={handleClearDisplayName}
                                />
                            )}
                            {showDisplayNameSuggestions && displayNameSuggestions.length > 0 && (
                                <ul className="absolute z-10 w-full bg-blanc dark:bg-dark border border-gray-300 rounded-md mt-1 max-h-40 overflow-y-auto shadow-lg">
                                    {displayNameSuggestions.map((suggestion, index) => (
                                        <li
                                            key={index}
                                            className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-dark-secondary cursor-pointer text-gray-700 dark:text-gray-300"
                                            onMouseDown={() => handleSuggestionClick(suggestion)} // onMouseDown pour se déclencher avant onBlur
                                        >
                                            {suggestion}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <button
                            type="submit"
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-orange text-blanc font-semibold rounded-md shadow hover:bg-framboise focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition duration-150 ease-in-out self-end"
                            disabled={isLoading}
                        >
                           <UserPlus size={18} /> Ajouter
                        </button>
                    </form>
                </div>

                {/* Colonne de Droite */}
                <div className="lg:w-2/3 flex flex-col">
                    <div className="mb-6 flex flex-col sm:flex-row justify-between items-center">
                        <SearchBar 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Rechercher un utilisateur..."
                        />
                    </div>
                     {/* Section Tableau Utilisateurs */}
                    <div className="flex-grow flex flex-col min-h-0 p-6 bg-blanc dark:bg-dark-secondary rounded-lg shadow-dropping-light dark:shadow-dropping-dark">
                        <h2 className="text-xl font-semibold text-gray-800 dark:text-blanc mb-3">Utilisateurs Autorisés</h2>

                        <div className="flex-grow overflow-auto min-h-0">
                            {isLoading ? (
                                <div className="flex justify-center items-center flex-grow">
                                    <p className="text-gray-500 dark:text-gray-400">Chargement des utilisateurs...</p>
                                </div>
                            ) : (
                                <TableComponent
                                    columns={columns}
                                    rows={tableRows}
                                    emptyText="Aucun utilisateur dans la whitelist."
                                    defaultSortConfig={{ key: 'addedAt', direction: 'descending' }}
                                />
                            )}
                        </div>
                        
                        {error && <div className="text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900 p-3 rounded-md mb-4">{error}</div>}
                    </div>
                 </div>
            </div>
        </div>
    );
};

export default ManagementAnnouncements;